

import React, { createContext, useState, useEffect } from "react";
import { cartLineKey } from "../Routes/Marketplace/libs/productSizes";
import {
  validateAddToCart,
  buildCartLineItem,
  getStockAvailable,
} from "../Routes/Marketplace/libs/cartStock";

export const CartContext = createContext({
  cartItems: [],
  addItemToCart: () => ({ success: false }),
  removeItemFromCart: () => {},
  clearItemFromCart: () => {},
  clearAllCart: () => {},
  replaceCartItems: () => {},
  cartCount: 0,
  totalCart: 0,
  isCartDropOpen: false,
  setIsCartDropOpen: () => {},
});

export const CartProvider = ({ children }) => {
  const storedCartItems = JSON.parse(localStorage.getItem("cartItems")) || [];
  const [cartItems, setCartItems] = useState(storedCartItems);
  const [cartCount, setCartCount] = useState(0);
  const [totalCart, setTotalCart] = useState(0);
  const [isCartDropOpen, setIsCartDropOpen] = useState(false);

  useEffect(() => {
    const newCartCount = cartItems.reduce((total, cartItem) => total + cartItem.quantity, 0);
    setCartCount(newCartCount);
  }, [cartItems]);

  useEffect(() => {
    const newCartTotal = cartItems.reduce(
      (total, cartItem) => total + cartItem.quantity * cartItem.price,
      0
    );
    setTotalCart(newCartTotal);
  }, [cartItems]);

  const addItemToCart = (productToAdd, quantity = 1) => {
    const qty = Math.max(1, parseInt(quantity, 10) || 1);
    const selectedSize = productToAdd.selectedSize ?? null;
    const lineKey = cartLineKey({ _id: productToAdd._id, selectedSize });
    const inCart = cartItems.find((item) => cartLineKey(item) === lineKey)?.quantity || 0;
    const hasStockMeta = productToAdd.stockAvailable != null || inCart === 0;
    const validation = hasStockMeta
      ? validateAddToCart(cartItems, productToAdd, qty)
      : { ok: true };

    if (!validation.ok) {
      return { success: false, message: validation.message };
    }

    const existingCartItemIndex = cartItems.findIndex(
      (cartItem) => cartLineKey(cartItem) === lineKey
    );

    if (existingCartItemIndex !== -1) {
      const updatedCartItems = [...cartItems];
      const existing = updatedCartItems[existingCartItemIndex];
      updatedCartItems[existingCartItemIndex] = {
        ...existing,
        quantity: existing.quantity + qty,
        stockAvailable:
          productToAdd.stockAvailable != null
            ? getStockAvailable(productToAdd)
            : existing.stockAvailable ?? getStockAvailable(productToAdd),
      };
      setCartItems(updatedCartItems);
    } else {
      setCartItems([
        ...cartItems,
        buildCartLineItem(productToAdd, qty, selectedSize),
      ]);
    }

    return { success: true };
  };

  const removeItemFromCart = (cartItemToRemove) => {
    const lineKey = cartLineKey(cartItemToRemove);
    const updatedCartItems = cartItems.map((cartItem) =>
      cartLineKey(cartItem) === lineKey
        ? { ...cartItem, quantity: cartItem.quantity - 1 }
        : cartItem
    );
    setCartItems(updatedCartItems.filter((cartItem) => cartItem.quantity > 0));
  };

  const clearItemFromCart = (cartItemToClear) => {
    const lineKey = cartLineKey(cartItemToClear);
    setCartItems(cartItems.filter((cartItem) => cartLineKey(cartItem) !== lineKey));
  };

  const clearAllCart = () => {
    setCartItems([]);
    localStorage.removeItem("cartItems");
  };

  const replaceCartItems = (items) => {
    setCartItems(Array.isArray(items) ? items : []);
  };

  useEffect(() => {
    localStorage.setItem("cartItems", JSON.stringify(cartItems));
  }, [cartItems]);

  const value = {
    cartItems,
    addItemToCart,
    removeItemFromCart,
    clearItemFromCart,
    clearAllCart,
    replaceCartItems,
    cartCount,
    totalCart,
    isCartDropOpen,
    setIsCartDropOpen,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
